import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import SEO from '../../SEO';
import { useAuth, isMentorUser, isAdminUser, getDashboardPath } from '../../../context/AuthContext';
import { MentorProvider } from './MentorContext';
import { MENTOR_NAV } from './nav';
import { LogOut, Menu, X, Sparkles, Bell } from 'lucide-react';
import portalApi from '../../../lib/portalApi';

function MentorShell() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
    if (user && !isMentorUser(user) && !isAdminUser(user)) {
      navigate(getDashboardPath(user));
    }
  }, [user, authLoading, navigate]);

  // Load persisted theme preference
  useEffect(() => {
    if (user) {
      portalApi.getMentorSettings().then((res) => {
        if (res?.appearance?.theme) {
          setTheme(res.appearance.theme);
        }
      }).catch(() => {});
    }
  }, [user]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (authLoading || !user) return null;

  const isLight = theme === 'light';

  return (
    <>
      <SEO title="Innovace Mentor Portal" description="Innovace mentor dashboard: KRA/KPI tracking, startups, calendar and tasks." />
      <div className={`min-h-screen ${isLight ? 'bg-slate-100 text-slate-900' : 'bg-[#08080A] text-[#F2F2F4]'} flex flex-col md:flex-row antialiased`}>
        {/* Mobile Header Bar */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-[#131318] border-b border-[#2C2C34] sticky top-0 z-30">
          <div>
            <div className="text-base font-bold tracking-tight text-[#F2F2F4]">Innovace</div>
            <div className="text-[11px] text-[#8E8E99]">Mentor portal</div>
          </div>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-[#8E8E99] hover:text-[#F2F2F4] rounded-lg border border-[#2C2C34]"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </header>

        {/* Sidebar Overlay for Mobile */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`fixed top-0 bottom-0 left-0 w-64 ${
            isLight ? 'bg-white border-slate-200' : 'bg-[#08080A] border-[#2C2C34]'
          } border-r flex flex-col z-50 transition-transform duration-200 md:translate-x-0 ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
          }`}
        >
          {/* Brand header matching PDF Page 2 */}
          <div className="p-6 border-b border-[#2C2C34]/60">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold tracking-tight text-[#F2F2F4]">Innovace</span>
              <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[#D42B92]/20 text-[#D42B92] border border-[#D42B92]/30">
                PRO
              </span>
            </div>
            <div className="text-xs text-[#8E8E99] mt-0.5">Mentor portal</div>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 p-3.5 space-y-1.5 overflow-y-auto">
            {MENTOR_NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? 'bg-[#321129] text-[#F2F2F4] border border-[#D42B92]/40 shadow-sm shadow-[#D42B92]/10 font-semibold'
                      : 'text-[#8E8E99] hover:bg-white/[0.04] hover:text-[#F2F2F4] border border-transparent'
                  }`
                }
              >
                <div className="flex items-center gap-3">
                  {item.icon && <item.icon className="w-4 h-4 shrink-0 text-[#8E8E99]" />}
                  <span>{item.label}</span>
                </div>
                {item.badge && (
                  <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* User profile & Sign Out matching PDF Page 2 */}
          <div className="p-4 border-t border-[#2C2C34]/60 bg-[#131318]/50">
            <div className="flex items-center gap-3 mb-3">
              {user.avatar ? (
                <img src={user.avatar} alt="" className="w-8 h-8 rounded-full object-cover border border-[#2C2C34]" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#321129] border border-[#D42B92]/30 flex items-center justify-center text-xs font-semibold text-[#D42B92]">
                  {(user.fullName || user.name || 'M').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#F2F2F4] truncate">{user.fullName || user.name || 'Mentor'}</div>
                <div className="text-xs text-[#8E8E99] truncate">{user.email}</div>
              </div>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs font-medium text-[#8E8E99] hover:text-[#F2F2F4] hover:bg-white/[0.05] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out</span>
            </button>
          </div>
        </aside>

        {/* Main Content View */}
        <main className="flex-1 md:ml-64 min-h-screen overflow-x-hidden">
          <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
            <Outlet context={{ theme, setTheme }} />
          </div>
        </main>
      </div>
    </>
  );
}

export default function MentorLayout() {
  return (
    <MentorProvider>
      <MentorShell />
    </MentorProvider>
  );
}
